import type { StudyMissionSelectDto, StudySelectDto } from '@@types/api.types';
import { BlueLabel } from '@components/shared/blue-label.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { type SimpleTab, SimpleTabs } from '@components/shared/simple-tabs.component';
import {
  useGetMeiaMissionThreads,
  useGetMeiaStudyMissionThreads,
  useGetMeiaStudyThreadSwimlanes,
  useGetMeiaStudyThreadTaskReadiness,
} from '@queries/meia.query';
import { CrosshairIcon, LayoutGridIcon } from 'lucide-react';
import { type FC, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { meiaRoute } from '../meia.constants';
import { MeiaMissionScopeStrip } from './meia-mission-scope-strip.component';
import { MeiaMissionTaskAuthoring } from './meia-mission-task-authoring.component';
import { MeiaP3AdvanceBar } from './meia-p3-advance-bar.component';
import { MeiaP3TargetEffectOverrides } from './meia-p3-target-effect-overrides.component';

type MeiaP3SubTab = 'canvas' | 'overrides';

const isMeiaP3SubTab = (value: string | null): value is MeiaP3SubTab => value === 'canvas' || value === 'overrides';

const subTabs: SimpleTab[] = [
  { icon: <LayoutGridIcon size={14} />, label: 'Task Canvas', value: 'canvas' },
  { icon: <CrosshairIcon size={14} />, label: 'Effect on Target · overrides', value: 'overrides' },
];

type MeiaP3AuthoringProps = {
  canEdit: boolean;
  isMissionsLoading?: boolean;
  missions: StudyMissionSelectDto[];
  study: StudySelectDto;
  studyId: string;
};

export const MeiaP3Authoring: FC<MeiaP3AuthoringProps> = (props) => {
  const { canEdit, isMissionsLoading, missions, study, studyId } = props;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedMissionId = searchParams.get('missionId');

  const subtabParam = searchParams.get('subtab');
  const activeSubtab: MeiaP3SubTab = isMeiaP3SubTab(subtabParam) ? subtabParam : 'canvas';

  const validMissionId =
    requestedMissionId && missions.some((mission) => mission.id === requestedMissionId)
      ? requestedMissionId
      : (missions[0]?.id ?? null);

  useEffect(() => {
    if (!validMissionId) return;
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (next.get('missionId') !== validMissionId) {
      next.set('missionId', validMissionId);
      changed = true;
    }
    if (changed) {
      setSearchParams(next, { preventScrollReset: true, replace: true });
    }
  }, [searchParams, setSearchParams, validMissionId]);

  const selectedMission = missions.find((mission) => mission.id === validMissionId);
  const { threads: allStudyThreads } = useGetMeiaStudyMissionThreads({ missions, studyId });
  const { data: missionThreads = [] } = useGetMeiaMissionThreads({ missionId: selectedMission?.id, studyId });
  const { swimlanes } = useGetMeiaStudyThreadSwimlanes({ studyId, threads: missionThreads });
  const { taskReadiness } = useGetMeiaStudyThreadTaskReadiness({ studyId, threads: missionThreads });

  const taskRows = useMemo(
    () => taskReadiness.map((row) => ({ swimlaneId: row.swimlaneId, threadId: row.threadId })),
    [taskReadiness],
  );

  const handleSelectSubtab = (value: string): void => {
    if (!isMeiaP3SubTab(value)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('subtab', value);
        return next;
      },
      { preventScrollReset: true, replace: false },
    );
  };

  const handleSelectMission = (missionId: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set('missionId', missionId);
    next.delete('threadId');
    setSearchParams(next, { preventScrollReset: true, replace: false });
  };

  const handleAddMissionInP1 = (): void => {
    navigate(meiaRoute.studyTab({ query: { action: 'addMission' }, studyId, tab: 'p1' }));
  };

  const handleAddMissionThreadInP2 = (): void => {
    if (!validMissionId) return;
    navigate(
      meiaRoute.studyTab({
        query: { action: 'addThread', missionId: validMissionId },
        studyId,
        tab: 'p2',
      }),
    );
  };

  if (!isMissionsLoading && missions.length === 0) {
    return (
      <EmptyStateContainer
        className="min-h-[520px] rounded-xl bg-white shadow-sm"
        message={canEdit ? 'Add Mission in P1' : undefined}
        onClick={canEdit ? handleAddMissionInP1 : undefined}
        title="No Missions"
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div>
        <BlueLabel
          className="text-xs"
          text="Phase 3 — Task Canvas"
        />
        <h2 className="mt-1 text-lg font-semibold text-gray-950">Assign tasks to kill-chain stages</h2>
        <p className="mt-1 text-xs text-gray-500">
          Drag cards between stages or click one to open it. Gate G3 needs every stage covered by at least one task with
          measure links.
        </p>
      </div>

      <SimpleTabs
        activeTab={activeSubtab}
        fullWidth={false}
        onTabChange={handleSelectSubtab}
        tabs={subTabs}
        variant="underline"
      />

      {activeSubtab === 'canvas' && (
        <>
          <MeiaMissionScopeStrip
            headerButton={canEdit ? { onClick: handleAddMissionInP1, text: '+ Add Mission in P1' } : undefined}
            missions={missions}
            onSelectMission={handleSelectMission}
            phase="p3"
            selectedMissionId={selectedMission?.id}
            study={study}
            threads={allStudyThreads}
          />

          {validMissionId && (
            <MeiaMissionTaskAuthoring
              canEdit={canEdit}
              emptyThreadAction={
                canEdit
                  ? {
                      message: 'Add Mission Thread in P2',
                      onClick: handleAddMissionThreadInP2,
                    }
                  : undefined
              }
              missionId={validMissionId}
              studyId={studyId}
            />
          )}

          {selectedMission && (
            <MeiaP3AdvanceBar
              mission={selectedMission}
              studyId={studyId}
              swimlanes={swimlanes}
              taskRows={taskRows}
              threads={missionThreads}
            />
          )}
        </>
      )}

      {activeSubtab === 'overrides' && (
        <MeiaP3TargetEffectOverrides
          missions={missions}
          study={study}
          threads={allStudyThreads}
        />
      )}
    </div>
  );
};
